import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import './App.css';

// --- PERSISTENCE & CONNECTION SETUP ---
const getSessionId = () => {
  let id = localStorage.getItem('bs_sessionId');
  if (!id) {
    id = 'user_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('bs_sessionId', id);
  }
  return id;
};

const SOCKET_URL = 'https://games.tolmanhome.us';
const SOCKET_PATH = '/blank-slate-api/socket.io';
const sessionId = getSessionId();

const socket = io(SOCKET_URL, {
  path: SOCKET_PATH,
  auth: { sessionId },
  autoConnect: false
});

// --- AUDIO HELPERS ---
const playSound = (type) => {
    const sounds = {
        tick: new Audio('/sounds/tick.mp3'),
        reveal: new Audio('/sounds/pop.mp3'),
        win: new Audio('/sounds/win.mp3'),
        chalk: new Audio('/sounds/chalk.mp3')
    };
    if (sounds[type]) {
        sounds[type].volume = 0.5;
        sounds[type].play().catch(e => console.log("Audio play failed (user interaction needed first)"));
    }
};

function App() {
  // --- STATE ---
  const [gameState, setGameState] = useState('home'); 
  const [playerName, setPlayerName] = useState(localStorage.getItem('bs_playerName') || '');
  const [roomCode, setRoomCode] = useState('');
  const [playerId, setPlayerId] = useState(sessionId);
  const [isHost, setIsHost] = useState(false);
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState({});
  const [availableRooms, setAvailableRooms] = useState([]); 
  
  // Game Play State
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [answer, setAnswer] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [reveals, setReveals] = useState([]);
  const [roundNumber, setRoundNumber] = useState(0);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [winner, setWinner] = useState(null);
  const [winnerScore, setWinnerScore] = useState(0);
  const [playerColors, setPlayerColors] = useState({});
  const [showRules, setShowRules] = useState(false);

  // Eraser Animation State
  const [erasing, setErasing] = useState(false);

  const chalkColors = [
    '#FFD700', '#00CED1', '#FF69B4', '#32CD32', '#FF8C00', '#BA55D3', 
    '#00BFFF', '#FF6347', '#7FFF00', '#FF1493', '#00FA9A', '#FFB6C1'
  ];

  const assignPlayerColors = (playersList) => {
    const colors = {};
    playersList.forEach((player, index) => {
      colors[player.id] = chalkColors[index % chalkColors.length];
    });
    setPlayerColors(colors);
  };

  // --- EFFECT: SOCKET LISTENERS ---
  useEffect(() => {
    if (!socket.connected) socket.connect();

    // Runs on initial connect AND every auto-reconnect (wifi blip, phone screen
    // lock) — the server needs a fresh rejoinGame each time to re-enter the room.
    const rejoinIfPossible = () => {
      const lastRoom = localStorage.getItem('bs_lastRoom');
      if (lastRoom) {
        socket.emit('rejoinGame', {
          roomCode: lastRoom,
          playerName: localStorage.getItem('bs_playerName') || ''
        });
      }
    };
    socket.on('connect', rejoinIfPossible);
    if (socket.connected) rejoinIfPossible();

    socket.on('roomList', (rooms) => setAvailableRooms(rooms));
    socket.on('roomCreated', (data) => handleJoin(data));
    socket.on('roomJoined', (data) => handleJoin(data));

    socket.on('playersUpdated', ({ players, scores }) => {
      setPlayers(players);
      setScores(scores);
      setTotalPlayers(players.length);
      assignPlayerColors(players);
    });

    socket.on('gameStarted', ({ prompt, roundNumber }) => {
      // ANIMATION ON: Starting a new game
      triggerEraser(() => {
          setCurrentPrompt(prompt);
          setRoundNumber(roundNumber);
          setGameState('playing');
          setAnswer('');
          setHasSubmitted(false);
          setSubmittedCount(0);
          setReveals([]);
          playSound('chalk');
      });
    });

    socket.on('timerUpdate', (time) => {
        setTimeRemaining(time);
        if (time === 10 || time <= 5) playSound('tick');
    });

    socket.on('submissionUpdate', ({ submittedCount, totalPlayers }) => {
      setSubmittedCount(submittedCount);
      setTotalPlayers(totalPlayers);
    });

    socket.on('revealAnswers', ({ submissions, scores }) => {
      setReveals(submissions);
      setScores(scores);
      setGameState('revealing');
      playSound('reveal');
    });

    socket.on('newRound', ({ prompt, roundNumber }) => {
      // ANIMATION OFF: Instant snap for next round
      setCurrentPrompt(prompt);
      setRoundNumber(roundNumber);
      setGameState('playing');
      setAnswer('');
      setHasSubmitted(false);
      setSubmittedCount(0);
      setReveals([]);
      setTimeRemaining(30);
    });

    socket.on('scoreUpdated', ({ scores }) => {
      setScores(scores);
    });

    socket.on('gameWon', ({ submissions, scores, winnerId, winnerName, winningScore }) => {
      setReveals(submissions);
      setScores(scores);
      setWinner({ id: winnerId, name: winnerName });
      setWinnerScore(winningScore);
      setGameState('winner');
      playSound('win');
    });

    socket.on('gameReset', ({ players, scores }) => {
      // ANIMATION ON: Cleaning up for Play Again
      triggerEraser(() => {
          setGameState('lobby');
          setPlayers(players);
          setScores(scores);
          setRoundNumber(0);
          setReveals([]);
          setWinner(null);
          setAnswer('');
          setHasSubmitted(false);
      });
    });

    socket.on('promotedToHost', () => setIsHost(true));
    
    socket.on('kicked', () => {
        alert("You were kicked from the room.");
        localStorage.removeItem('bs_lastRoom');
        setGameState('home');
        setRoomCode('');
    });

    socket.on('error', ({ message }) => {
      if (message.includes('expired') || message.includes('not found')) {
        localStorage.removeItem('bs_lastRoom');
        setGameState('home');
      }
      setError(message);
      setTimeout(() => setError(''), 3000);
    });

    return () => {
      socket.off('connect', rejoinIfPossible);
      socket.off('roomList');
      socket.off('roomCreated');
      socket.off('roomJoined');
      socket.off('playersUpdated');
      socket.off('gameStarted');
      socket.off('timerUpdate');
      socket.off('submissionUpdate');
      socket.off('revealAnswers');
      socket.off('newRound');
      socket.off('scoreUpdated');
      socket.off('gameWon');
      socket.off('gameReset');
      socket.off('promotedToHost');
      socket.off('kicked');
      socket.off('error');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eraser Animation Helper
  const triggerEraser = (callback) => {
      setErasing(true);
      setTimeout(() => {
          callback();
          setTimeout(() => setErasing(false), 500); 
      }, 500); 
  };

  // --- ACTIONS ---
  const handleJoin = (data) => {
    setRoomCode(data.roomCode);
    setPlayerId(data.playerId);
    setIsHost(data.isHost);
    setGameState(data.gameState || 'lobby');
    localStorage.setItem('bs_lastRoom', data.roomCode);
    localStorage.setItem('bs_playerName', playerName);
  };

  const createRoom = () => {
    if (playerName.trim()) socket.emit('createRoom', playerName.trim());
    else setError("Please enter your name first");
  };

  const joinRoom = (code) => {
    if (playerName.trim()) socket.emit('joinRoom', { roomCode: code, playerName: playerName.trim() });
    else setError("Please enter your name first");
  };

  const leaveGame = () => {
    if (window.confirm("Are you sure you want to leave?")) {
      socket.emit('leaveRoom', { roomCode });
      localStorage.removeItem('bs_lastRoom');
      setGameState('home');
      setRoomCode('');
      setPlayers([]);
      setScores({});
    }
  };
  
  const kickPlayer = (targetId) => {
      if(window.confirm("Kick this player?")) {
          socket.emit('kickPlayer', { roomCode, targetId });
      }
  };

  const startGame = () => socket.emit('startGame', roomCode);

  const submitAnswer = () => {
    if (answer.trim()) {
      socket.emit('submitAnswer', { roomCode, answer: answer.trim() });
      setHasSubmitted(true);
      playSound('chalk');
    }
  };

  const nextRound = () => socket.emit('nextRound', roomCode);
  const handlePlayAgain = () => socket.emit('playAgain', roomCode);

  const updatePlayerScore = (pid, delta) => {
    const current = scores[pid] || 0;
    socket.emit('updateScore', { roomCode, playerId: pid, newScore: Math.max(0, current + delta) });
  };

  const resetGame = () => {
    socket.emit('leaveRoom', { roomCode }); 
    localStorage.removeItem('bs_lastRoom');
    setGameState('home');
    setRoomCode('');
    setPlayerId('');
    setIsHost(false);
    setPlayers([]);
    setScores({});
    setWinner(null);
  };

  // --- RENDERERS ---

  if (gameState === 'home') {
    return (
      <div className="App">
        <div className="container">
          <div className="logo-container">
            <div className="logo">
              <div className="logo-text"><span className="logo-blank">___</span> SLATE</div>
              <div className="logo-tagline">Fill the Blank • Match to Win</div>
            </div>
          </div>
          {error && <div className="error">{error}</div>}
          <div className="home-section">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => {
                setPlayerName(e.target.value);
                localStorage.setItem('bs_playerName', e.target.value);
              }}
              className="input"
              maxLength={20}
            />
            <button onClick={createRoom} className="btn btn-primary" disabled={!playerName.trim()}>
              Create New Game
            </button>
            <div className="divider">OR JOIN A GAME</div>
            <div className="room-list">
               {availableRooms.length === 0 ? (
                 <div className="no-rooms">No active games found...</div> 
               ) : (
                 availableRooms.map(room => (
                   <div key={room.code} className="room-item" onClick={() => joinRoom(room.code)}>
                     <div>
                       <div className="room-host">{room.hostName}'s Game</div>
                       <div className="room-meta">{room.playerCount}/12 Players</div>
                     </div>
                     <button className="join-btn">JOIN</button>
                   </div>
                 ))
               )}
            </div>
          </div>
        </div>
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  // Lobby
  if (gameState === 'lobby') {
    return (
      <div className="App">
        {erasing && <div className="eraser-overlay"></div>}
        <div className="container">
          <div className="logo-container">
            <div className="logo logo-small">
              <div className="logo-text"><span className="logo-blank">___</span> SLATE</div>
            </div>
          </div>
          {error && <div className="error">{error}</div>}
          <div className="players-section">
            <h2>Players ({players.length})</h2>
            <ul className="players-list">
              {players.map(player => (
                <li key={player.id} className={`player-item ${player.isOffline ? 'offline' : ''}`}>
                  <span>
                     {player.name} {player.id === playerId && '(You)'}
                     {player.isOffline && ' (Signal Lost)'}
                     {player.id === players[0]?.id && ' 👑'}
                  </span>
                  {/* KICK BUTTON FOR HOST */}
                  {isHost && player.id !== playerId && (
                      <button className="kick-btn" onClick={() => kickPlayer(player.id)}>✕</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {isHost ? (
            <button onClick={startGame} className="btn btn-primary" disabled={players.length < 2}>Start Game</button>
          ) : (
            <p className="waiting-text">Waiting for host to start the game...</p>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  // Playing
  if (gameState === 'playing') {
    const timerColor = timeRemaining <= 10 ? 'timer-warning' : '';
    return (
      <div className="App">
        {erasing && <div className="eraser-overlay"></div>}
        <div className="container">
          <div className="game-header">
            <div className="logo logo-mini">
              <span className="logo-text-mini"><span className="logo-blank-mini">___</span> SLATE</span>
            </div>
            <div className="round-info">Round {roundNumber}</div>
          </div>
          <div className={`timer ${timerColor}`}>⏱️ {timeRemaining}s</div>
          <div className="scoreboard-mini">
            {players.map(player => (
              <div key={player.id} className="score-item-mini">
                <span style={{ color: playerColors[player.id] || '#fff' }}>{player.name}:</span> {scores[player.id] || 0}
              </div>
            ))}
          </div>
          <div className="prompt-section">
            <h2 className="prompt">{currentPrompt}</h2>
          </div>
          {!hasSubmitted ? (
            <div className="answer-section">
              <input
                type="text"
                placeholder="Type your answer..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitAnswer()} // <--- ENTER KEY SUPPORT ADDED
                className="input answer-input"
                style={{ color: playerColors[playerId] || '#fff' }}
                maxLength={30}
              />
              <button onClick={submitAnswer} className="btn btn-primary" disabled={!answer.trim()}>Lock In Answer</button>
            </div>
          ) : (
            <div className="waiting-section">
              <p className="waiting-text">✓ Answer submitted!</p>
              <p className="submission-count">Waiting for others... ({submittedCount}/{totalPlayers})</p>
            </div>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  // Revealing
  if (gameState === 'revealing') {
    return (
      <div className="App">
        <div className="container">
          <div className="results-header">
            <div className="logo logo-mini">
              <span className="logo-text-mini"><span className="logo-blank-mini">___</span> SLATE</span>
            </div>
            <h2 className="results-title">Round {roundNumber} Results</h2>
          </div>
          <div className="reveals-section">
            {reveals.map((reveal, index) => (
              <div key={index} className="reveal-card">
                <div className="reveal-player" style={{ color: playerColors[reveal.playerId] || '#fff' }}>{reveal.playerName}</div>
                <div className="reveal-answer">{reveal.noAnswer ? <em className="no-answer">No answer</em> : `"${reveal.answer.toUpperCase()}"`}</div>
                <div className={`reveal-points points-${reveal.points}`}>
                  +{reveal.points} points
                  {reveal.points === 3 && ' 🎯'}
                  {reveal.points === 1 && ' 💥'}
                </div>
              </div>
            ))}
          </div>
          <div className="scoreboard">
            <h3>Current Scores {isHost && <span className="host-hint">(Click ± to adjust)</span>}</h3>
            {players.sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)).map(player => (
                <div key={player.id} className="score-item">
                  <span style={{ color: playerColors[player.id] || '#fff' }}>{player.name}</span>
                  <div className="score-controls">
                    {isHost && <button className="score-btn score-minus" onClick={() => updatePlayerScore(player.id, -1)}>-</button>}
                    <span className="score-value">{scores[player.id] || 0}</span>
                    {isHost && <button className="score-btn score-plus" onClick={() => updatePlayerScore(player.id, 1)}>+</button>}
                  </div>
                </div>
              ))}
          </div>
          {isHost ? (
            <button onClick={nextRound} className="btn btn-primary">Next Round</button>
          ) : (
            <p className="waiting-text">Waiting for host to start next round...</p>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  // Winner
  if (gameState === 'winner') {
    const isWinner = winner && winner.id === playerId;
    const sortedPlayers = [...players].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0));
    return (
      <div className="App winner-screen">
        {isWinner && (
          <>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
            <div className="confetti"></div><div className="confetti"></div><div className="confetti"></div>
          </>
        )}
        <div className="container winner-container">
          <div className="winner-announcement">
            <div className="trophy-icon">🏆</div>
            <h1 className="winner-title">{isWinner ? "YOU WIN!" : `${winner?.name} WINS!`}</h1>
            <div className="winner-name">{winner?.name}</div>
            <div className="winner-score">{winnerScore} Points!</div>
          </div>
          <div className="last-round-section">
            <h3>Final Round Results</h3>
            <div className="reveals-section">
              {reveals.map((reveal, index) => (
                <div key={index} className="reveal-card">
                  <div className="reveal-player" style={{ color: playerColors[reveal.playerId] || '#fff' }}>{reveal.playerName}</div>
                  <div className="reveal-answer">{reveal.noAnswer ? <em className="no-answer">No answer</em> : `"${reveal.answer.toUpperCase()}"`}</div>
                  <div className={`reveal-points points-${reveal.points}`}>+{reveal.points}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="final-standings">
            <h3>Final Standings</h3>
            {sortedPlayers.map((player, index) => (
              <div key={player.id} className={`standing-item ${player.id === winner?.id ? 'winner-row' : ''}`}>
                <span className="standing-rank">{index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}</span>
                <span className="standing-name">{player.name}</span>
                <span className="standing-score">{scores[player.id] || 0}</span>
              </div>
            ))}
          </div>
          {isHost ? (
            <button onClick={handlePlayAgain} className="btn btn-primary btn-large">Play Again</button>
          ) : (
            <div className="waiting-text" style={{marginTop: '20px'}}>Waiting for host to Play Again...</div>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }

  if (gameState === 'finished') {
    const sortedPlayers = [...players].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0));
    return (
      <div className="App">
        <div className="container">
          <h2 className="game-over-title">🏆 Game Over! 🏆</h2>
          <div className="final-scores">
            <h2>Final Scores</h2>
            {sortedPlayers.map((player, index) => (
              <div key={player.id} className="final-score-item">
                <span className="rank">#{index + 1}</span>
                <span className="player-name">{player.name} {index === 0 && ' 🏆'}</span>
                <span className="score-value">{scores[player.id] || 0}</span>
              </div>
            ))}
          </div>
          {isHost ? <button onClick={handlePlayAgain} className="btn btn-primary">Play Again</button> : <div className="waiting-text">Waiting for host...</div>}
          <button onClick={resetGame} className="btn btn-text" style={{marginTop: '10px'}}>Back to Home</button>
        </div>
		<RulesButton onClick={() => setShowRules(true)} />
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </div>
    );
  }
  return null;
}

const LeaveButton = ({ onClick }) => (
  <div className="leave-game-container">
    <button onClick={onClick} className="leave-game-btn">Leave Game</button>
  </div>
);

const RulesModal = ({ onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" onClick={e => e.stopPropagation()}>
      <button className="modal-close" onClick={onClose}>✕</button>
      <h2 className="modal-title">How to Play</h2>
      
      <div className="rules-section">
        <h3>Objective</h3>
        <p>Fill in the blank to match exactly <strong>one</strong> other player to score points!</p>
      </div>

      <div className="rules-section">
        <h3>Scoring</h3>
        <ul className="rules-list">
          <li><strong>3 Points:</strong> Match exactly <strong>ONE</strong> other player. (Best!)</li>
          <li><strong>1 Point:</strong> Match <strong>MORE</strong> than one player.</li>
          <li><strong>0 Points:</strong> No matches at all.</li>
        </ul>
      </div>

      <div className="rules-section">
        <h3>Winning</h3>
        <p>The first player to reach <strong>25 points</strong> wins the game!</p>
      </div>
      
      <button className="btn btn-primary" style={{marginTop: '20px', width: '100%'}} onClick={onClose}>
        Got it!
      </button>
    </div>
  </div>
);

const RulesButton = ({ onClick }) => (
  <button className="floating-rules-btn" onClick={onClick} title="Game Rules">?</button>
);

export default App;